import { IntlFormatters } from 'react-intl';
import { formTexts } from 'i18n';
import { validateWeight } from 'helpers/reviewCycles/form';
import getLengthOfInvisibleChars from '../getLengthOfInvisibleChars';

type FormatMessage = IntlFormatters['formatMessage'];
export type InlineValidator = (value: string) => true | string;

const NAME_MAX_LENGTH = 256;
const DESCRIPTION_MAX_LENGTH = 3000;

const isNotEmpty = (value: string) => {
  const lengthOfInvisibleChars = getLengthOfInvisibleChars(value);
  const onlyInvisibleChars = lengthOfInvisibleChars === value.length;
  return value.trim() !== '' && !onlyInvisibleChars;
};

export const makeNameValidator = (
  siblings: { name: string }[],
  currentIndex: number,
  entity: 'goal' | 'subgoal',
  formatMessage: FormatMessage,
): InlineValidator => {
  const type = entity === 'subgoal' ? 'subgoal' : 'scorecard';
  return (value = '') => {
    if (!isNotEmpty(value)) {
      return formatMessage(formTexts.errorRequired);
    }
    if (value.length > NAME_MAX_LENGTH) {
      return formatMessage(formTexts.errorMaxLength, {
        fieldName: '',
        maxLength: NAME_MAX_LENGTH,
      });
    }
    const trimmed = value.trim();
    const hasDuplicate = siblings.some(
      (item, idx) => idx !== currentIndex && item.name.trim() === trimmed,
    );
    if (hasDuplicate) {
      return formatMessage(
        {
          id: 'components.GoalForm.uniqueNameError',
          defaultMessage: `{type, select,
              subgoal {Subgoal}
              other {Goal}
            } with such name already exists in this {type, select,
              template {template}
              scorecard {scorecard}
              other {goal}
            }.`,
        },
        { type },
      );
    }
    return true;
  };
};

export const makeDescriptionValidator =
  (formatMessage: FormatMessage): InlineValidator =>
  (value = '') => {
    if (value.length > DESCRIPTION_MAX_LENGTH) {
      return formatMessage(formTexts.errorMaxLength, {
        fieldName: '',
        maxLength: DESCRIPTION_MAX_LENGTH,
      });
    }
    return true;
  };

export const makeWeightValidator =
  (formatMessage: FormatMessage): InlineValidator =>
  (value = '') => {
    const result = validateWeight(value, formatMessage);
    return result === true ? true : result;
  };
